import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  ColorEnum,
  WidgetTemplatesVisibilityEnum,
  WidgetTemplateUpdateInputObject,
  WidgetTemplatesKindEnum,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import Form, {
  ActionsProps,
} from '@lib/components/ReactHookForm/FormContainer';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { IconName } from '@lib/components/Modal/enums';
import { materialSymbolsOutlined } from '@lib/enums/common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import { AttachmentIntendedUse } from '@lib/enums/attachments';
import findAttachmentByIntendedUse, {
  AttachmentWithIntendedUse,
} from 'routes/Home/utils/findAttachmentByIntendedUse';
import prepareAttachmentInputs, {
  AttachmentInput,
} from 'routes/Home/utils/prepareAttachmentInputs';
import WidgetTemplateFormActions from './WidgetTemplateFormActions';
import getWidgetTemplateFormFields from './utils/getWidgetTemplateFormFields';
import getWidgetTemplateDefaultValues from './utils/getWidgetTemplateDefaultValues';
import WidgetTemplatePreviewModal from '../WidgetTemplatePreviewModal';
import styles from './WidgetTemplateForm.module.scss';

interface WidgetTemplateFormTemplate {
  id: string;
  kind: WidgetTemplatesKindEnum;
  title: string | null;
  description?: string | null;
  visibility: WidgetTemplatesVisibilityEnum;
  fields?: {
    backgroundColor?: string | null;
    content?: string | null;
    link?: string | null;
  } | null;
  attachmentsAttached?: AttachmentWithIntendedUse[] | null;
}

interface WidgetTemplateFormProps {
  template: WidgetTemplateFormTemplate;
  deleteLoading?: boolean;
  loading?: boolean;
  onSave: (id: string, attributes: WidgetTemplateUpdateInputObject) => void;
  onDelete: (id: string) => void;
  onCancel: () => void;
  isViewOnly?: boolean;
}

export default function WidgetTemplateForm({
  template,
  deleteLoading,
  loading,
  onSave,
  onDelete,
  onCancel,
  isViewOnly,
}: WidgetTemplateFormProps) {
  const { t } = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Initialize preview values with template defaults
  const defaultValues = useMemo(
    () => getWidgetTemplateDefaultValues(template, t),
    [template, t],
  );

  // Track defaultValues reference to detect when we need to reset preview values
  // This replaces the useEffect anti-pattern with React 18's recommended
  // "update state during render" pattern
  const prevDefaultValuesRef = useRef(defaultValues);
  const [previewFormValues, setPreviewFormValues] =
    useState<Values>(defaultValues);

  // Reset preview values when defaultValues changes (new reference from useMemo)
  if (prevDefaultValuesRef.current !== defaultValues) {
    prevDefaultValuesRef.current = defaultValues;
    setPreviewFormValues(defaultValues);
  }

  // Update preview form values on any field change
  // Merge with existing values to preserve fields not included in the update
  const handleFieldChange = useCallback((values: Values) => {
    setPreviewFormValues((prev) => ({ ...prev, ...values }));
  }, []);

  // Preview handler - opens modal with already synced form values
  const handlePreviewClick = useCallback(() => {
    setIsPreviewModalOpen(true);
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
  }, []);

  const fields = useMemo(
    () =>
      getWidgetTemplateFormFields({
        t,
        widgetKind: template.kind,
        onPreviewClick: handlePreviewClick,
        onFieldChange: handleFieldChange,
      }),
    [t, template.kind, handlePreviewClick, handleFieldChange],
  );

  const handleSubmit = (values: Values) => {
    const attributes: WidgetTemplateUpdateInputObject = {};
    const attachmentsToSend: AttachmentInput[] = [];

    // Title
    const title = _get(values, 'title') as string | undefined;
    if (title !== undefined) {
      attributes.title = title;
    }

    // Visibility
    const isPrivate = _get(values, 'isPrivate') as boolean | undefined;
    attributes.visibility = isPrivate
      ? WidgetTemplatesVisibilityEnum.Prv
      : WidgetTemplatesVisibilityEnum.Pub;

    // Description
    const description = _get(values, 'description') as string | undefined;
    if (description !== undefined) {
      attributes.description = description;
    }

    // Fields based on widget kind
    const { kind } = template;

    if (kind === WidgetTemplatesKindEnum.ExternalLink) {
      const link = _get(values, 'link') as string | undefined;
      attributes.fields = {
        link: link || undefined,
      };
    }

    // Handle icon image attachment
    const iconImageValue = _get(values, 'iconImage') as
      Record<string, unknown> | null | undefined;
    const originalIconAttachment = findAttachmentByIntendedUse(
      template.attachmentsAttached,
      AttachmentIntendedUse.WidgetIcon,
    );
    const iconAttachmentInputs = prepareAttachmentInputs(
      iconImageValue,
      originalIconAttachment,
      AttachmentIntendedUse.WidgetIcon,
    );
    attachmentsToSend.push(...iconAttachmentInputs);

    if (kind === WidgetTemplatesKindEnum.Announcement) {
      const backgroundColor = _get(values, ['backgroundColor', 'value']) as
        ColorEnum | undefined;
      const content = _get(values, 'content') as string | undefined;
      attributes.fields = {
        backgroundColor: backgroundColor || undefined,
        content: content || undefined,
      };

      // Handle image attachment
      const imageValue = _get(values, 'image') as
        Record<string, unknown> | null | undefined;
      const originalImageAttachment = findAttachmentByIntendedUse(
        template.attachmentsAttached,
        AttachmentIntendedUse.WidgetImage,
      );
      const imageAttachmentInputs = prepareAttachmentInputs(
        imageValue,
        originalImageAttachment,
        AttachmentIntendedUse.WidgetImage,
      );
      attachmentsToSend.push(...imageAttachmentInputs);
    }

    // Set attachmentsAttached only if there are attachments to send
    if (attachmentsToSend.length > 0) {
      attributes.attachmentsAttached =
        attachmentsToSend as WidgetTemplateUpdateInputObject['attachmentsAttached'];
    }

    onSave(template.id, attributes);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = () => {
    onDelete(template.id);
  };

  const actionsComponent: (actionsProps: ActionsProps) => React.ReactElement = (
    actionsProps,
  ) => (
    <WidgetTemplateFormActions
      {...actionsProps}
      onCancel={onCancel}
      isViewOnly={isViewOnly}
    />
  );

  return (
    <>
      <WhiteBox verticalPaddings="large">
        <Form
          actions={actionsComponent}
          actionsComponentProps={{
            onCancel,
            isViewOnly,
          }}
          defaultValues={defaultValues}
          fields={fields}
          formId={template.id || 'widget-template-form'}
          loading={loading}
          onSubmit={handleSubmit}
        />
      </WhiteBox>
      {!isViewOnly && (
        <div className={entityViewStyles.actions}>
          <Button
            buttonText={t('home.delete-widget')}
            buttonType={ButtonTypes.negativeOutlined}
            leftIcon="delete"
            leftIconClassName={materialSymbolsOutlined}
            onClick={handleOpenDeleteModal}
          />
        </div>
      )}
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={t('home.delete-widget-template-text')}
        icon={IconName.Delete}
        isOpen={isDeleteModalOpen}
        loading={deleteLoading}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('are-you-sure')}
        modalContentClassName={styles.modalContentClassName}
        useProgress
      >
        <SupportiveMessage
          text={t('home.delete-widget-template-warning')}
          color={SupportiveMessageColor.Yellow}
          hasBottomSpace={false}
        />
      </ConfirmationModal>
      <WidgetTemplatePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={handleClosePreviewModal}
        formValues={previewFormValues}
      />
    </>
  );
}
